

































































































































































































import {
	computed,
	defineComponent,
	PropType,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { useTranslate } from '@front-app/hooks/useTranslate';
import { parseIntFromInt } from '@front-app/util/number';
import { FullUser, ProfessionalExperienceRequest } from '@front-app/services/UserApi/UserModel';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { Spacing } from '@front-app/components/constants';
import { v } from '@front-app/components/form/validation/useValidation';
import { required } from '@front-app/components/form/validation/validators';
import EditorWrapper from '@front-app/components/layouts/form/EditorWrapper.vue';
import FormSection from '@front-app/components/form/FormSection.vue';
import FormLabel from '@front-app/components/form/FormLabel.vue';
import FormError from '@front-app/components/form/FormError.vue';
import FormInput from '@front-app/components/form/FormInput.vue';
import CountryPicker from '@front-app/components/inputs/CountryPicker.vue';
import RegionPicker from '@front-app/components/inputs/RegionPicker.vue';
import MonthPicker from '@front-app/components/inputs/MonthPicker.vue';
import { CurrentUserStore } from '@front-app/store/singletons/User';
import { unparseProfessionalExperience } from '@front-app/services/parse';
import { ApiMonthOption } from '@front-app/components/inputs/monthPickerUtil';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import { CountryCode, RegionCode } from '@front-app/services/SharedModels';
import { useFocusFirstInvalidField } from '@front-app/hooks/useFocusFirstInvalidField';
import { DEFAULT_COUNTRY_CODE, EMPTY_REGION } from '@front-app/components/inputs/CountryPickerData';

enum ModeType {
	EDIT,
	CREATE,
}

const HEADER_LABEL = {
	[ModeType.CREATE]: 'user-profile.add-professional-experience',
	[ModeType.EDIT]: 'user-profile.edit-your-professional-experience',
};

export default defineComponent({
	components: {
		EditorWrapper,
		FormSection,
		FormLabel,
		FormError,
		FormInput,
		CountryPicker,
		RegionPicker,
		MonthPicker,
		CtaButton,
	},
	props: {
		user: {
			type: Object as PropType<FullUser>,
			required: true,
		},
		experienceToEditId: {
			type: Number as PropType<number | undefined>,
			required: false,
			default: undefined,
		},
	},
	setup(props, ctx) {
		const $t = useTranslate(ctx);
		const currentMode = computed(() => (props.experienceToEditId === undefined ? ModeType.CREATE : ModeType.EDIT));
		const headerLabel = computed(() => $t(HEADER_LABEL[currentMode.value]));

		const positionTitle = ref('');
		const organization = ref('');
		const startMonth = ref(ApiMonthOption.NONE);
		const startYear: Ref<string | number | null> = ref(null);
		const endMonth = ref(ApiMonthOption.NONE);
		const endYear: Ref<string | number | null> = ref(null);
		const country = ref(DEFAULT_COUNTRY_CODE);
		const region = ref(EMPTY_REGION);
		const city = ref('');

		const experienceBeingEdited = computed(() => props.user.professionalExperiences
			.find(({ id }) => id === props.experienceToEditId));

		watch(experienceBeingEdited, (curr, prev) => {
			if (!curr && prev !== curr) {
				positionTitle.value = '';
				organization.value = '';
				startMonth.value = ApiMonthOption.NONE;
				startYear.value = null;
				endMonth.value = ApiMonthOption.NONE;
				endYear.value = null;
				country.value = DEFAULT_COUNTRY_CODE;
				region.value = EMPTY_REGION;
				city.value = '';
			} else if (curr) {
				positionTitle.value = curr.positionTitle;
				organization.value = curr.employer.name;
				startMonth.value = curr.startMonth as unknown as ApiMonthOption;
				startYear.value = curr.startYear;
				endMonth.value = curr.endMonth as unknown as ApiMonthOption;
				endYear.value = curr.endYear;
				country.value = curr.location.country as unknown as CountryCode;
				region.value = curr.location.provStateRegion as unknown as RegionCode;
				city.value = curr.location.city;
			}
		}, { immediate: true });

		const maxYear = new Date().getFullYear();

		const validation = v({
			organization: {
				value: organization,
				rules: [required()],
			},
			// Custom validation that collapses all the date month fields such that either
			// ALL must be present OR NONE are present. If any one is selected, the rest must
			// all be selected.
			fromTo: {
				value: computed(() => {
					const startMonthVal = startMonth.value === -1 ? null : startMonth.value;
					const startYearVal = startYear.value === '' ? null : startYear.value;
					const endMonthVal = endMonth.value === -1 ? null : endMonth.value;
					const endYearVal = endYear.value === '' ? null : endYear.value;
					const vals = [
						startMonthVal,
						startYearVal,
						endMonthVal,
						endYearVal,
					];
					return vals.every((val) => val === null) || vals.every((val) => val !== null);
				}),
				rules: [
					required(),
				],
			},
			startYearGreater: {
				value: computed(() => {
					const startYearVal = startYear.value;
					const endYearVal = endYear.value;
					if (startYearVal && endYearVal) {
						return parseIntFromInt(startYearVal, 10) <= parseIntFromInt(endYearVal, 10);
					}
					return true;
				}),
				rules: [required()],
			},
			yearMaxDate: {
				value: computed(() => {
					const startYearVal = startYear.value;
					const endYearVal = endYear.value;
					if (startYearVal && endYearVal) {
						return parseIntFromInt(startYearVal, 10) <= maxYear && parseIntFromInt(endYearVal, 10) <= maxYear;
					}
					return true;
				}),
				rules: [required()],
			},
			country: {
				value: country,
				rules: [required()],
			},
			city: {
				value: city,
				rules: [required()],
			},
		});

		const {
			track,
			isLoading,
			isError,
			isSuccess,
		} = useAsyncStatus();

		const getExperienceUpdateRequest = (): ProfessionalExperienceRequest => {
			const startMonthVal = startMonth.value;
			const endMonthVal = endMonth.value;
			const base: ProfessionalExperienceRequest = {
				positionTitle: positionTitle.value,
				startMonth: startMonthVal.toString(),
				startYear: (startYear.value ?? '-1').toString(),
				endMonth: endMonthVal.toString(),
				endYear: (endYear.value ?? '-1').toString(),
				employer: {
					name: organization.value,
				},
				location: {
					city: city.value,
					province: region.value as unknown as RegionCode,
					country: country.value as unknown as CountryCode,
				},
			};
			if (props.experienceToEditId) {
				base.id = props.experienceToEditId;
			}
			return base;
		};

		const {
			focusFirstInvalid,
		} = useFocusFirstInvalidField({ containerSelector: '.edit-professional-experience-form' });

		const saveUser = () => {
			validation.validate();
			if (!validation.isValid.value) {
				focusFirstInvalid();
				return;
			}
			track(CurrentUserStore.actions.update({
				professionalExperiences: [
					getExperienceUpdateRequest(),
					...props.user.professionalExperiences.filter(({ id }) => id !== props.experienceToEditId).map(unparseProfessionalExperience),
				],
			}));
		};

		const deleteExperience = async () => {
			// eslint-disable-next-line no-alert
			const shouldProceed = window.confirm('Permanently remove this professional experience?');
			if (!shouldProceed) {
				return;
			}
			const professionalExperiences = props.user.professionalExperiences
				.filter(({ id }) => id !== props.experienceToEditId)
				.map(unparseProfessionalExperience);

			await track(CurrentUserStore.actions.update({
				professionalExperiences,
			}));

			ctx.emit('close');
		};

		return {
			headerLabel,
			currentMode,
			// Requests
			isLoading,
			isError,
			isSuccess,
			saveUser,
			deleteExperience,
			validation,
			// Field values
			positionTitle,
			organization,
			startMonth,
			startYear,
			endMonth,
			endYear,
			country,
			region,
			city,
			// Util
			ModeType,
			maxYear,
			Spacing,
		};
	},
});
